import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import { loadPersistedData } from '../../Shared/lib/utils';
import { RootState } from '../../Shared/Redux/store';

const initialState = {
  editStudentData: null,
  editStudentDocument: null,
  editStudentBillData: null,
  isAddStudentModalOpen: false,
  isAssignModalOpen: false,
  metaData: loadPersistedData('metaData', null),
  showEditStudentForm: false,
  showStudentBillForm: false,
  showStudentPaymentForm: false,
  showStudentCancellationPaymentForm: false,
  genders: loadPersistedData('genders', null),
  relationships: loadPersistedData('relationships', null),
  classes: loadPersistedData('classes', null),
  levels: loadPersistedData('levels', null),
  showStudentClassModal: false,
  showStudentFileModal: false,
  changeClassData: null,
  cancelPaymentId: null,
};

const StudentSlice = createSlice({
  name: 'student',
  initialState,
  reducers: {
    setEditStudentData: (state, action: PayloadAction<any>) => {
      state.editStudentData = action.payload;
    },
    setEditStudentDocument: (state, action: PayloadAction<any>) => {
      state.editStudentDocument = action.payload;
    },
    setShowstudentClassModal: state => {
      state.showStudentClassModal = !state.showStudentClassModal;
    },
    setChangeClassData: (state, action: PayloadAction<any>) => {
      state.changeClassData = action.payload;
    },
    setEditStudentBillData: (state, action: PayloadAction<any>) => {
      state.editStudentBillData = action.payload;
    },
    setIsAddStudentModalOpen: state => {
      state.isAddStudentModalOpen = !state.isAddStudentModalOpen;
    },
    setMetaData: (state, action: PayloadAction<any>) => {
      state.metaData = action.payload;
      state.genders = Object.entries(action.payload?.gender || {}).map(
        ([key, value]) => ({
          label: value,
          value: key.toString(),
        })
      );
      state.relationships = Object.entries(
        action.payload?.relationships || {}
      ).map(([key, value]) => ({
        label: value,
        value: key.toString(),
      }));
      state.classes = Object.entries(action.payload?.classes || {}).map(
        ([key, value]) => ({
          label: value,
          value: key.toString(),
        })
      );
      state.levels = Object.entries(action.payload?.levels || {}).map(
        ([key, value]) => ({
          label: value,
          value: key.toString(),
        })
      );
      localStorage.setItem('metaData', JSON.stringify(state.metaData));
      localStorage.setItem('genders', JSON.stringify(state.genders));
      localStorage.setItem(
        'relationships',
        JSON.stringify(state.relationships)
      );
      localStorage.setItem('classes', JSON.stringify(state.classes));
      localStorage.setItem('levels', JSON.stringify(state.levels));
    },
    setShowEditStudentForm: state => {
      state.showEditStudentForm = !state.showEditStudentForm;
    },
    setShowStudentBillForm: state => {
      state.showStudentBillForm = !state.showStudentBillForm;
    },
    setShowStudentPaymentForm: state => {
      state.showStudentPaymentForm = !state.showStudentPaymentForm;
    },
    setShowStudentCancellationPaymentForm: state => {
      state.showStudentCancellationPaymentForm =
        !state.showStudentCancellationPaymentForm;
    },
    setCancelPaymentId: (state, action: PayloadAction<any>) => {
      state.cancelPaymentId = action.payload;
    },
    setShowStudentFileModal: state => {
      state.showStudentFileModal = !state.showStudentFileModal;
    },
  },
});

export const selectIsAddStudentModalOpen = (state: RootState) =>
  state.student.isAddStudentModalOpen;

export const selectEditStudentDocument = (state: RootState) =>
  state.student.editStudentDocument;

export const selectEditStudentData = (state: RootState) =>
  state.student.editStudentData;

export const selectEditStudentBillData = (state: RootState) =>
  state.student.editStudentBillData;

export const selectMetaData = (state: RootState) => state.student.metaData;

export const selectShowEditStudentForm = (state: RootState) =>
  state.student.showEditStudentForm;

export const selectShowStudentBillForm = (state: RootState) =>
  state.student.showStudentBillForm;

export const selectGenders = (state: RootState) => state.student.genders;

export const selectRelationships = (state: RootState) =>
  state.student.relationships;

export const selectClasses = (state: RootState) => state.student.classes;

export const selectLevels = (state: RootState) => state.student.levels;

export const selectShowStudentClassModal = (state: RootState) =>
  state.student.showStudentClassModal;

export const selectChangeClassData = (state: RootState) =>
  state.student.changeClassData;

export const selectShowStudentPaymentForm = (state: RootState) =>
  state.student.showStudentPaymentForm;

export const selectShowStudentCancellationPaymentForm = (state: RootState) =>
  state.student.showStudentCancellationPaymentForm;

export const selectCancelPaymentId = (state: RootState) =>
  state.student.cancelPaymentId;

export const selectShowStudentFileModal = (state: RootState) =>
  state.student.showStudentFileModal;

export const {
  setEditStudentData,
  setIsAddStudentModalOpen,
  setMetaData,
  setShowEditStudentForm,
  setEditStudentBillData,
  setShowStudentBillForm,
  setShowstudentClassModal,
  setChangeClassData,
  setShowStudentPaymentForm,
  setShowStudentCancellationPaymentForm,
  setCancelPaymentId,
  setShowStudentFileModal,
  setEditStudentDocument,
} = StudentSlice.actions;

export default StudentSlice.reducer;
